import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseurl } from "../../config/url";

const PAGE_SIZE = 20;

const getToken = () => {
  const t1 = localStorage.getItem("adminToken");
  const t2 = localStorage.getItem("token");
  if (t1) return t1;
  if (t2) return t2;

  try {
    return JSON.parse(localStorage.getItem("auth") || "{}")?.token || "";
  } catch {
    return "";
  }
};

const safeText = (v) => (v === null || v === undefined || v === "" ? "N/A" : String(v));

const formatINR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "N/A";
  return `₹${n.toLocaleString("en-IN")}`;
};

const dtIN = (d) => {
  if (!d) return "N/A";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "N/A";
  return x.toLocaleString("en-IN");
};

const pickCategory = (p) => p?.category?.name || p?.categoryName || "N/A";
const pickSubcategory = (p) => p?.subcategory?.name || p?.subcategoryName || "N/A";

const pickSeller = (p) => p?.seller || p?.sellerId || p?.createdBy || p?.userId || null;

const pickSellerId = (p) => {
  const s = pickSeller(p);
  if (!s) return "";
  if (typeof s === "string") return s;
  return s?._id || s?.id || "";
};

const pickSellerName = (p) => {
  const s = pickSeller(p);
  if (!s) return "N/A";
  if (typeof s === "string") return "Seller";
  return s?.name || s?.fullName || s?.companyName || s?.shopName || "N/A";
};

const ApprovedProducts = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters + pagination
  const [search, setSearch] = useState("");
  const [saleTypeFilter, setSaleTypeFilter] = useState("all"); // all | Sale By Seller | Sale By OGCS
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest"); // newest|oldest|price-asc|price-desc|name-asc|name-desc
  const [page, setPage] = useState(1);

  // ✅ Fetch approved products (same endpoint as your Approve page)
  useEffect(() => {
    const fetchApprovedProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();
        const res = await axios.get(`${baseurl}/api/admin/products`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          withCredentials: true,
        });

        const list = Array.isArray(res.data?.products) ? res.data.products : [];
        const approved = list.filter((p) => !!p?.approved);

        setProducts(approved);
      } catch (err) {
        console.error("Fetch approved products error:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Failed to fetch approved products");
      } finally {
        setLoading(false);
      }
    };

    fetchApprovedProducts();
  }, []);

  // ✅ Category dropdown options (unique + safe)
  const categoryOptions = useMemo(() => {
    const map = new Map(); // id -> name
    products.forEach((p) => {
      const id = p?.category?._id;
      const name = p?.category?.name;
      if (id && !map.has(id)) map.set(id, name || "Unnamed");
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [products]);

  // ✅ Search + filter + sort
  const filtered = useMemo(() => {
    let list = [...products];
    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((p) => {
        const name = (p?.name || "").toLowerCase();
        const cat = pickCategory(p).toLowerCase();
        const sub = pickSubcategory(p).toLowerCase();
        const sellerName = pickSellerName(p).toLowerCase();
        const vis = safeText(p?.visibilityLevel).toLowerCase();
        const saleType = safeText(p?.saleType).toLowerCase();

        return (
          name.includes(q) ||
          cat.includes(q) ||
          sub.includes(q) ||
          sellerName.includes(q) ||
          vis.includes(q) ||
          saleType.includes(q)
        );
      });
    }

    if (saleTypeFilter !== "all") {
      list = list.filter((p) => p?.saleType === saleTypeFilter);
    }

    if (categoryFilter !== "all") {
      list = list.filter((p) => p?.category?._id === categoryFilter);
    }

    const byDateDesc = (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
    const byDateAsc = (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0);
    const byPriceAsc = (a, b) => Number(a?.price || 0) - Number(b?.price || 0);
    const byPriceDesc = (a, b) => Number(b?.price || 0) - Number(a?.price || 0);
    const byNameAsc = (a, b) => safeText(a?.name).localeCompare(safeText(b?.name));
    const byNameDesc = (a, b) => safeText(b?.name).localeCompare(safeText(a?.name));

    if (sortOrder === "newest") list.sort(byDateDesc);
    if (sortOrder === "oldest") list.sort(byDateAsc);
    if (sortOrder === "price-asc") list.sort(byPriceAsc);
    if (sortOrder === "price-desc") list.sort(byPriceDesc);
    if (sortOrder === "name-asc") list.sort(byNameAsc);
    if (sortOrder === "name-desc") list.sort(byNameDesc);

    return list;
  }, [products, search, saleTypeFilter, categoryFilter, sortOrder]);

  // reset page when filters change
  useEffect(() => setPage(1), [search, saleTypeFilter, categoryFilter, sortOrder]);

  // ✅ Pagination
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(startIndex, startIndex + PAGE_SIZE);
  const from = totalItems === 0 ? 0 : startIndex + 1;
  const to = Math.min(startIndex + PAGE_SIZE, totalItems);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  // ✅ View product details (same endpoint)
  const handleViewProduct = async (productId) => {
    try {
      setDetailsLoading(true);
      setError("");

      const token = getToken();
      const res = await axios.get(`${baseurl}/api/admin/product-details/${productId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        withCredentials: true,
      });

      const productData = res.data?.product || res.data;
      setSelectedProduct(productData);
    } catch (err) {
      console.error("View product error:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to fetch product details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeSidebar = () => setSelectedProduct(null);

  // ✅ build image url (supports your object image storage)
  const buildImageUrl = (img) => {
    if (!img) return "";
    if (typeof img === "string") return img;
    if (typeof img === "object" && img._id) return `${baseurl}/api/image/upload/${img._id}`;
    return "";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading approved products…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-10">
      <div className="relative flex gap-6">
        {/* LEFT */}
        <div className="flex-1 bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Approved Products</h1>
              <p className="text-sm text-gray-600 mt-1">
                Search, filter and view details of approved products.
              </p>
            </div>

            <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Total: <b>{products.length}</b> • Showing: <b>{filtered.length}</b>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-500">Search</label>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Product / Category / Seller / Visibility..."
                  className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Sale Type</label>
                <select
                  value={saleTypeFilter}
                  onChange={(e) => setSaleTypeFilter(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="all">All</option>
                  <option value="Sale By Seller">Sale By Seller</option>
                  <option value="Sale By OGCS">Sale By OGCS</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-500">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="all">All</option>
                  {categoryOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-500">Sort</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="price-asc">Price Low → High</option>
                  <option value="price-desc">Price High → Low</option>
                  <option value="name-asc">Name A → Z</option>
                  <option value="name-desc">Name Z → A</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
              <div className="text-sm text-gray-600">
                Showing <b>{from}</b> to <b>{to}</b> of <b>{totalItems}</b>
              </div>

              <button
                onClick={() => {
                  setSearch("");
                  setSaleTypeFilter("all");
                  setCategoryFilter("all");
                  setSortOrder("newest");
                }}
                className="px-4 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100"
              >
                Clear Filters
              </button>
            </div>

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Product</th>
                  <th className="px-4 py-3 text-left font-semibold">Category</th>
                  <th className="px-4 py-3 text-left font-semibold">Seller</th>
                  <th className="px-4 py-3 text-left font-semibold">Price</th>
                  <th className="px-4 py-3 text-left font-semibold">Stock</th>
                  <th className="px-4 py-3 text-left font-semibold">Created</th>
                  <th className="px-4 py-3 text-right font-semibold">Action</th>
                </tr>
              </thead>

              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                      No approved products found.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((p) => (
                    <tr key={p._id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{safeText(p.name)}</div>
                        <div className="text-xs text-gray-500 font-mono break-all">{safeText(p._id)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">
                            {safeText(p.saleType || "N/A")}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-gray-700">
                        <div className="text-gray-900">{pickCategory(p)}</div>
                        <div className="text-xs text-gray-500">{pickSubcategory(p)}</div>
                      </td>

                      <td className="px-4 py-3 text-gray-700">
                        <div className="text-gray-900 font-semibold">{pickSellerName(p)}</div>
                        <div className="text-xs text-gray-500 font-mono break-all">
                          {pickSellerId(p) ? `ID: ${pickSellerId(p)}` : ""}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-gray-700">{formatINR(p.price)}</td>
                      <td className="px-4 py-3 text-gray-700">{safeText(p.stock)}</td>
                      <td className="px-4 py-3 text-gray-700">{dtIN(p.createdAt)}</td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2 flex-wrap">
                          <button
                            onClick={() => handleViewProduct(p._id)}
                            className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          >
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/products/details/${p._id}`)}
                            className="px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
                          >
                            Open Page
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalItems > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Page <b>{safePage}</b> of <b>{totalPages}</b>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Prev
                  </button>

                  {(() => {
                    const maxBtns = 7;
                    let start = Math.max(1, safePage - Math.floor(maxBtns / 2));
                    let end = Math.min(totalPages, start + maxBtns - 1);
                    start = Math.max(1, end - maxBtns + 1);

                    const pages = [];
                    for (let i = start; i <= end; i++) pages.push(i);

                    return pages.map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-2 rounded-lg border ${
                          p === safePage
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                        }`}
                      >
                        {p}
                      </button>
                    ));
                  })()}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Sidebar */}
        {selectedProduct && (
          <div className="fixed right-0 top-0 h-full w-[420px] bg-white border-l border-gray-200 shadow-2xl p-5 overflow-y-auto z-50">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl"
              onClick={closeSidebar}
              title="Close"
            >
              &times;
            </button>

            <div className="pr-8">
              <h2 className="text-xl font-bold text-gray-900">{safeText(selectedProduct.name)}</h2>
              <div className="text-sm text-gray-500 mt-1">
                {pickCategory(selectedProduct)} • {pickSubcategory(selectedProduct)}
              </div>
            </div>

            {detailsLoading ? (
              <div className="mt-6 text-sm text-gray-600">Loading details…</div>
            ) : (
              <div className="mt-6 space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <InfoCard label="Price" value={formatINR(selectedProduct.price)} />
                  <InfoCard label="Stock" value={safeText(selectedProduct.stock)} />
                  <InfoCard label="Max Qty" value={safeText(selectedProduct.maxquantity)} />
                  <InfoCard label="Sold" value={safeText(selectedProduct.sold || 0)} />
                  <InfoCard label="HSN" value={safeText(selectedProduct.hsnCode)} />
                  <InfoCard label="Visibility" value={safeText(selectedProduct.visibilityLevel)} />
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-500">Description</div>
                  <div className="text-sm text-gray-800 mt-1 leading-relaxed">
                    {selectedProduct.description || "No description available."}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-500">Specifications</div>
                  <ul className="list-disc list-inside mt-2 text-sm text-gray-800 space-y-1">
                    {selectedProduct.specifications?.length > 0 ? (
                      selectedProduct.specifications.map((spec, i) => (
                        <li key={i}>
                          <span className="font-semibold">{safeText(spec.key)}:</span>{" "}
                          {safeText(spec.value)}
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500">No specifications listed.</li>
                    )}
                  </ul>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-500">Seller / Created By</div>
                  <div className="text-base font-semibold text-gray-900 mt-1">
                    {pickSellerName(selectedProduct)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 font-mono break-all">
                    {pickSellerId(selectedProduct) ? `Seller ID: ${pickSellerId(selectedProduct)}` : ""}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-2">Images</div>
                  {selectedProduct.images?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {selectedProduct.images.map((img, idx) => {
                        const url = buildImageUrl(img);
                        return (
                          <div
                            key={idx}
                            className="w-full h-28 bg-gray-100 rounded-lg overflow-hidden border border-gray-200"
                          >
                            {url ? (
                              <img
                                src={url}
                                alt={`Product ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = "/placeholder.png";
                                }}
                              />
                            ) : (
                              <div className="h-full flex items-center justify-center text-xs text-red-600">
                                Invalid image
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No images available.</div>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => navigate(`/products/details/${selectedProduct._id}`)}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-900 text-white font-semibold hover:bg-black"
                  >
                    Open Product Page
                  </button>
                  <button
                    onClick={closeSidebar}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm font-semibold text-gray-900 mt-1 break-all">{safeText(value)}</div>
  </div>
);

export default ApprovedProducts;